import { Button, Input } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDocuments,
  useDeposerDocument,
  useDocument,
  useTelechargerDocument,
} from '../../../api/coffre.js';
import { estApiError } from '../../../api/errors.js';
import { ouvrirUrlSignee } from '../../../api/client.js';

export function PieceProvenance({ galerie, id }: { galerie: string; id: string }) {
  const { t } = useTranslation(['regie', 'commun']);
  const document = useDocument(galerie, id);
  const ouvrir = useTelechargerDocument(galerie);
  return (
    <span>
      <Button
        variant="ghost"
        loading={ouvrir.isPending}
        onClick={() => {
          ouvrir.mutate({ id }, { onSuccess: (r) => ouvrirUrlSignee(r.telechargement.url) });
        }}
      >
        {document.data?.document.titre ?? id}
      </Button>
      {(ouvrir.isError || document.isError) && <span role="alert">{t('commun:erreurReseau')}</span>}
    </span>
  );
}

export function PiecesProvenance({
  galerie,
  oeuvreId,
  pieces,
  peutDeposer,
  onChange,
  onDepotEnCours,
}: {
  galerie: string;
  oeuvreId: string;
  pieces: string[];
  peutDeposer: boolean;
  onChange: (ids: string[]) => void;
  onDepotEnCours: (enCours: boolean) => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const tr = (cle: string) => t(`regie:oeuvres.provenance.${cle}`);
  const [recherche, setRecherche] = useState('');
  const documents = useDocuments(galerie, { q: recherche, parPage: 100 });
  const deposer = useDeposerDocument(galerie);
  return (
    <fieldset>
      <legend>{tr('pieces')}</legend>
      <Input
        aria-label={tr('chercherPiece')}
        value={recherche}
        onChange={(e) => setRecherche(e.target.value)}
      />
      {pieces.map((id) => (
        <div key={id}>
          <PieceProvenance galerie={galerie} id={id} />
          <Button variant="ghost" onClick={() => onChange(pieces.filter((p) => p !== id))}>
            {tr('retirer')}
          </Button>
        </div>
      ))}
      <select
        aria-label={tr('ajouterPiece')}
        value=""
        disabled={pieces.length >= 20}
        onChange={(e) => {
          if (e.target.value) onChange([...pieces, e.target.value]);
        }}
      >
        <option value="">{tr('ajouterPiece')}</option>
        {documents.data?.documents
          .filter((d) => !pieces.includes(d.id))
          .map((d) => (
            <option key={d.id} value={d.id}>
              {d.titre}
            </option>
          ))}
      </select>
      {peutDeposer && pieces.length < 20 && (
        <label>
          {tr('deposerPiece')}
          <input
            type="file"
            disabled={deposer.isPending}
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              if (!fichier) return;
              onDepotEnCours(true);
              deposer.mutate(
                { fichier, titre: fichier.name, rattachements: [`artwork:${oeuvreId}`] },
                {
                  onSuccess: (r) => onChange([...pieces, r.document.id]),
                  onSettled: () => onDepotEnCours(false),
                },
              );
              e.target.value = '';
            }}
          />
        </label>
      )}
      {(documents.isError || deposer.isError) && (
        <p role="alert">
          {estApiError(deposer.error) ? deposer.error.message : t('commun:erreurReseau')}
        </p>
      )}
    </fieldset>
  );
}
